import { CommonModule, DecimalPipe } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule } from '@angular/common/http';
import { CUSTOM_ELEMENTS_SCHEMA, NgModule, NO_ERRORS_SCHEMA } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialogModule } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouterModule } from '@angular/router';
import { LibComponentsModule } from '@dr-customer-offers-ui/lib-components';
import { ENVIRONMENT } from '@dr-customer-offers-ui/lib-services';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { MapTimePipe, ValidateNumberDirective } from '@ngx-interval-data-grid';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { HighchartsChartModule } from 'highcharts-angular';
import { NgxDateRangePickerModule } from 'ngx-date-range-picker';
import { NgxDropdownModule } from 'ngx-dropdown';
import { NgxGlobalContactFooterModule } from 'ngx-global-contact-footer';
import { AnalyticsService, NOC_ANALYTICS } from 'ngx-shared-services';
import { analytics } from 'noc-analytics';
import { environment } from '../environments/environment';
import { AppComponent } from './app.component';
import { appRoutes } from './app.routes';
import { BulkOffersComponent } from './components/bulk-offers/bulk-offers.component';
import { BulkSelectionComponent } from './components/dialogs/bulk-selection/bulk-selection.component';
import { ExportReportComponent } from './components/dialogs/export/export-report.component';
import { ReasonCodeConfirmComponent } from './components/dialogs/reason-code-confirm/reason-code-confirm.component';
import { FiltersComponent } from './components/filters/filters.component';
import { GlobalAlertModule } from './components/global-alert/global-alert.module';
import { HourMinutePickerComponent } from './components/hour-minute-picker/hour-minute-picker.component';
import { NoDataComponent } from './components/no-data/no-data.component';
import { TwoDigitFormatPipe } from './components/pipes/twoDigitFormat.pipe';
import { BaselinesTabTableComponent } from './components/tables/baselines-tab/baselines-tab-table.component';
import { ClearedChartComponent } from './components/tables/cleared-tab/chart/cleared-tab-chart.component';
import { ClearedContainerComponent } from './components/tables/cleared-tab/cleared-container.component';
import { ClearedTabTableComponent } from './components/tables/cleared-tab/table/cleared-tab-table.component';
import { DeclaredAvailabilityTabTableComponent } from './components/tables/da-tab/da-tab-table.component';
import { OpenGraphTableComponent } from './components/tables/open-tab/graph/component/open-graph-table/open-graph-table.component';
import { OpenGraphComponent } from './components/tables/open-tab/graph/open-graph.component';
import { OpenContainerComponent } from './components/tables/open-tab/open-container.component';
import { OpenTableComponent } from './components/tables/open-tab/table/open-table.component';
import { ScheduleTabTableComponent } from './components/tables/schedule-tab/schedule-tab-table.component';
import { TabsComponent } from './components/tabs/tabs.component';
import { ToastComponent } from './components/toast/toast.component';
import { ClickOutsideDirective } from './shared/directives/click-outside.directive';
import { IconsComponent } from './shared/icons/icons.component';
import { AppInterceptor } from './shared/interceptors/app.interceptor';
import { ScrollToHeaderDirective, ScrollToTopDirective } from './shared/pipes/scroll-to-top.directive';
import { SuffixDatePipe } from './shared/pipes/suffix-date.pipe';
import { WeekDayPipe } from './shared/pipes/week-days.pipe';
import { metaReducers, reducers } from './store';
import { COMEffects } from './store/effects/com.effects';

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, '/assets/i18n/', '.json');
}

const materialModules = [
  MatButtonModule,
  MatButtonToggleModule,
  MatCardModule,
  MatCheckboxModule,
  MatDialogModule,
  MatDividerModule,
  MatDatepickerModule,
  MatIconModule,
  MatInputModule,
  MatListModule,
  MatMenuModule,
  MatProgressSpinnerModule,
  MatSelectModule,
  MatSidenavModule,
  MatTableModule,
  MatTabsModule,
  MatToolbarModule,
  MatTooltipModule
];

@NgModule({
  declarations: [
    AppComponent,
    FiltersComponent,
    TabsComponent,
    BulkOffersComponent,
    BulkSelectionComponent,
    ClearedTabTableComponent,
    OpenContainerComponent,
    OpenGraphComponent,
    OpenTableComponent,
    DeclaredAvailabilityTabTableComponent,
    ScheduleTabTableComponent,
    BaselinesTabTableComponent,
    HourMinutePickerComponent,
    ExportReportComponent,
    ReasonCodeConfirmComponent,
    TwoDigitFormatPipe,
    ScrollToTopDirective,
    ScrollToHeaderDirective,
    ClickOutsideDirective,
    ClearedChartComponent
  ],
  imports: [
    HighchartsChartModule,
    NoDataComponent,
    BrowserModule,

    // NgRx Store
    StoreModule.forRoot(reducers, {
      metaReducers,
      runtimeChecks: {
        strictStateImmutability: true,
        strictActionImmutability: true,
        strictStateSerializability: true,
        strictActionSerializability: true,
        strictActionWithinNgZone: true,
        strictActionTypeUniqueness: true
      }
    }),

    // NgRx Effects
    EffectsModule.forRoot([COMEffects]),

    // NgRx Store Devtools (only in development)
    StoreDevtoolsModule.instrument({
      maxAge: 25,
      logOnly: environment.production
    }),

    FormsModule,
    ReactiveFormsModule,
    CommonModule,
    BrowserAnimationsModule,
    FormsModule,
    RouterModule.forRoot(appRoutes, { initialNavigation: 'enabledBlocking', onSameUrlNavigation: 'reload' }),
    HttpClientModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient]
      }
    }),
    NgxGlobalContactFooterModule.forRoot({ namespace: 'ngx-global-contact-footer.' }),
    GlobalAlertModule,
    LibComponentsModule,
    NgxDropdownModule,
    NgxDateRangePickerModule,
    ...materialModules,
    MapTimePipe,
    ValidateNumberDirective,
    SuffixDatePipe,
    WeekDayPipe,
    IconsComponent,
    ToastComponent,
    ClearedContainerComponent,
    OpenGraphTableComponent,
    DecimalPipe
  ],
  providers: [
    TranslateService,
    HttpClient,
    AnalyticsService,
    { provide: NOC_ANALYTICS, useValue: analytics },
    { provide: ENVIRONMENT, useValue: environment },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AppInterceptor,
      multi: true
    }
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA],
  bootstrap: [AppComponent]
})
export class AppModule {}
